import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate, inr } from "../lib/api";
import TopBar from "../components/TopBar";
import { Button, Card, Chip, Empty, Input, Sheet, Skeleton, Textarea } from "../components/ui";
import {
  ArrowLeft, Calendar, MapPin, Clock, Check, X, HelpCircle,
  Users2, Shuffle, Lock, Trash2, Star, ArrowLeftRight, Send, Crown,
  ExternalLink, Edit, Activity, BarChart3, UserPlus,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./MembersPage";
import PollWhatsAppShare from "../components/PollWhatsAppShare";
import ScoreListCard from "../components/ScoreListCard";

export default function PollDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { club } = useAuth();
  const canManage = ["owner", "admin", "manager"].includes(club?.my_role);

  const [poll, setPoll] = useState(null);
  const [members, setMembers] = useState([]);
  const [respondOpen, setRespondOpen] = useState(false);
  const [chargesOpen, setChargesOpen] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);
  const [promoting, setPromoting] = useState(null);
  const [guestOpen, setGuestOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: m }] = await Promise.all([
        api.get(`/polls/${id}`),
        api.get("/members"),
      ]);
      setPoll(p);
      setMembers(m);
    } catch (err) { toast.error(apiError(err)); nav("/polls"); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const memberById = (mid) => members.find((x) => x.id === mid);
  const memberName = (mid) => memberById(mid)?.name || "—";

  const isClosed = poll?.status === "closed";
  const isDraft = poll?.status === "draft";

  const respond = async (status, comment = "", member_id = undefined) => {
    try {
      await api.post(`/polls/${id}/respond`, { status, comment, member_id });
      toast.success("Response recorded");
      setRespondOpen(false);
      await load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const closePoll = async () => {
    if (!window.confirm("Close this poll? Members won't be able to change their response.")) return;
    try { await api.post(`/polls/${id}/close`); toast.success("Poll closed"); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const reopenPoll = async () => {
    try { await api.post(`/polls/${id}/reopen`); toast.success("Poll re-opened"); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const generateTeams = async () => {
    try { await api.post(`/polls/${id}/generate-teams`); toast.success("Teams generated"); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const removePlayer = async (member) => {
    const what = member.guest
      ? `Remove guest ${member.name} from this poll?`
      : `Remove ${member.name}? They'll be marked OUT and taken out of the teams.`;
    const charges = poll.charges_posted ? "\n\nCharges were already posted — their match fee won't be reversed." : "";
    if (!window.confirm(what + charges)) return;
    try {
      const { data } = await api.delete(`/polls/${id}/players/${member.id}`);
      setPoll(data);
      toast.success(`${member.name} removed`);
    } catch (err) { toast.error(apiError(err)); }
  };
  const lockTeams = async () => {
    try { await api.post(`/polls/${id}/lock-teams`); toast.success("Teams locked"); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const deletePoll = async () => {
    if (!window.confirm("Delete this poll? Related charges will be reversed.")) return;
    try { await api.delete(`/polls/${id}`); toast.success("Poll deleted"); nav("/polls"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const promoteWaitingPlayer = async (responseId) => {
    if (!canManage) return;
    try {
      setPromoting(responseId);
      await api.post(`/polls/${id}/promote-waiting/${responseId}`);
      toast.success("Player promoted to squad ✓");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPromoting(null);
    }
  };

  if (!poll) {
    return (
      <div className="pb-24 px-5 pt-6">
        <Skeleton className="h-8 w-40 mb-4" />
        <Skeleton className="h-40 mb-3" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  const responsesByMember = new Map((poll.responses || []).map((r) => [r.member_id, r]));
  const inMembers = (poll.responses || []).filter((r) => r.status === "in");
  const waitingMembers = (poll.responses || []).filter((r) => r.status === "waiting");
  const outMembers = (poll.responses || []).filter((r) => r.status === "out");
  const maybeMembers = (poll.responses || []).filter((r) => r.status === "maybe");
  const pendingMembers = members.filter((m) => !responsesByMember.has(m.id) && m.active && !m.guest);

  return (
    <div className="pb-28">
      <div className="flex items-center justify-between px-3 pt-4">
        <button onClick={() => nav(-1)} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center" data-testid="back-btn">
          <ArrowLeft size={20}/>
        </button>
        {canManage && (
          <div className="flex items-center gap-1">
            {!poll.charges_posted && (
              <button onClick={() => nav(`/polls/${id}/edit`)} className="h-10 w-10 rounded-full hover:bg-blue-50 text-blue-600 grid place-items-center" data-testid="poll-edit-btn" title="Edit poll">
                <Edit size={18}/>
              </button>
            )}
            <button onClick={deletePoll} className="h-10 w-10 rounded-full hover:bg-red-50 text-red-500 grid place-items-center" data-testid="poll-delete-btn">
              <Trash2 size={18}/>
            </button>
          </div>
        )}
      </div>

      <TopBar title={poll.title} subtitle={<span className="capitalize">{poll.match_type} • {poll.status}</span>} />

      <div className="px-5 space-y-4">
        {/* Share on WhatsApp — one-tap, no integration cost */}
        <PollWhatsAppShare poll={poll} className="w-full" size="md" />

        {/* Meta */}
        <Card>
          <div className="grid grid-cols-2 gap-4">
            <Meta icon={Calendar} label="Date" value={fmtDate(poll.date)} />
            <Meta icon={Clock} label="Time" value={
              poll.start_time && poll.end_time
                ? `${poll.start_time.substring(0, 5)}–${poll.end_time.substring(0, 5)}`
                : poll.time || "—"
            } />
            <Meta icon={MapPin} label="Ground" value={poll.ground} />
            <Meta icon={Users2} label="Players" value={`${poll.min_players}–${poll.max_players}`} />
          </div>
          {poll.ground_location && (
            <a href={poll.ground_location} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-brand text-sm font-semibold" data-testid="poll-map-link">
              Open in Maps <ExternalLink size={13}/>
            </a>
          )}
          {poll.notes && <div className="mt-3 pt-3 border-t border-black/[0.06] text-sm text-ink-muted">{poll.notes}</div>}
          {(poll.match_fee > 0 || poll.sponsor_contribution > 0) && (
            <div className="mt-3 pt-3 border-t border-black/[0.06] flex items-center justify-between text-sm">
              <span className="text-ink-muted">Est. match fee</span>
              <span className="font-bold">{inr(poll.match_fee)}/head</span>
            </div>
          )}
        </Card>

        {/* Counts */}
        <div className="grid grid-cols-4 gap-2">
          <Stat label="IN" value={poll.counts?.in ?? 0} tone="success" />
          <Stat label="OUT" value={poll.counts?.out ?? 0} tone="danger" />
          <Stat label="MAYBE" value={poll.counts?.maybe ?? 0} tone="warning" />
          <Stat label="Pending" value={poll.counts?.pending ?? 0} />
        </div>

        {/* My response */}
        {poll.my_member_id && !isClosed && !isDraft && (
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold">
                Your response
              </div>
              {poll.my_response && (
                <Chip tone={poll.my_response.status === "in" ? "success" : poll.my_response.status === "out" ? "danger" : "warning"}>
                  {poll.my_response.status.toUpperCase()}
                </Chip>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <VoteBtn label="IN" tone="success" icon={Check} onClick={() => setRespondOpen("in")} active={poll.my_response?.status === "in"} testid="vote-in" />
              <VoteBtn label="OUT" tone="danger" icon={X} onClick={() => setRespondOpen("out")} active={poll.my_response?.status === "out"} testid="vote-out" />
              <VoteBtn label="MAYBE" tone="warning" icon={HelpCircle} onClick={() => setRespondOpen("maybe")} active={poll.my_response?.status === "maybe"} testid="vote-maybe" />
            </div>
          </Card>
        )}

        {/* Admin actions */}
        {canManage && (
          <Card>
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-3">
              Admin actions
            </div>
            <div className="grid grid-cols-2 gap-2">
              {isClosed ? (
                <Button variant="outline" onClick={reopenPoll} data-testid="poll-reopen-btn">Re-open</Button>
              ) : (
                <Button variant="outline" onClick={closePoll} data-testid="poll-close-btn">Close poll</Button>
              )}
              <Button variant={poll.teams ? "outline" : "primary"} onClick={generateTeams} disabled={poll.teams?.locked} data-testid="poll-generate-teams-btn">
                <Shuffle size={16}/> {poll.teams ? "Shuffle" : "Generate"}
              </Button>
              {poll.teams && !poll.teams.locked && (
                <>
                  <Button variant="outline" onClick={() => setSwapOpen(true)} data-testid="poll-swap-btn">
                    <ArrowLeftRight size={16}/> Swap
                  </Button>
                  <Button variant="dark" onClick={lockTeams} data-testid="poll-lock-btn">
                    <Lock size={16}/> Lock teams
                  </Button>
                </>
              )}
              {poll.teams && (
                <div className="col-span-2">
                  <PollWhatsAppShare
                    poll={poll}
                    teams={{
                      ...poll.teams,
                      team_a: poll.teams.team_a?.map((id) => memberById(id) || { id, name: "—" }),
                      team_b: poll.teams.team_b?.map((id) => memberById(id) || { id, name: "—" }),
                      captain_a: memberById(poll.teams.captain_a) || { id: poll.teams.captain_a, name: "—" },
                      captain_b: memberById(poll.teams.captain_b) || { id: poll.teams.captain_b, name: "—" },
                      vc_a: memberById(poll.teams.vc_a) || { id: poll.teams.vc_a, name: "—" },
                      vc_b: memberById(poll.teams.vc_b) || { id: poll.teams.vc_b, name: "—" },
                      common: poll.teams.common ? memberById(poll.teams.common) || { id: poll.teams.common, name: "—" } : null,
                    }}
                    label="Share teams on WhatsApp"
                    className="w-full"
                    size="md"
                    data-testid="poll-share-teams-btn"
                  />
                </div>
              )}
              <Button variant="outline" onClick={() => setGuestOpen(true)} className="col-span-2" data-testid="poll-add-guest-btn">
                <UserPlus size={16}/> Add guest player
              </Button>
              {!poll.charges_posted && inMembers.length > 0 && (
                <Button variant="dark" onClick={() => setChargesOpen(true)} className="col-span-2" data-testid="poll-post-charges-btn">
                  <Send size={16}/> Post charges to {inMembers.length} players
                </Button>
              )}
              {poll.charges_posted && (
                <div className="col-span-2 text-center text-xs text-brand font-semibold py-2">
                  ✓ Charges posted — {inr(poll.charges_per_head)}/player
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Teams */}
        {poll.teams && (
          <TeamsView teams={poll.teams} memberById={memberById} />
        )}

        <MatchDay pollId={id} />

        {/* Response Lists */}
        <ResponseList
          title={`Coming (${inMembers.length})`} tone="success" items={inMembers} memberById={memberById} testid="list-in"
          onRemove={canManage ? removePlayer : null}
        />
        {waitingMembers.length > 0 && (
          <ResponseList title={`Waiting List (${waitingMembers.length})`} tone="warning" items={waitingMembers} memberById={memberById} testid="list-waiting" badge="WAITING" canManage={canManage} promoting={promoting} onPromote={promoteWaitingPlayer} />
        )}
        {maybeMembers.length > 0 && (
          <ResponseList title={`Maybe (${maybeMembers.length})`} tone="warning" items={maybeMembers} memberById={memberById} testid="list-maybe" />
        )}
        {outMembers.length > 0 && (
          <ResponseList title={`Not Coming (${outMembers.length})`} tone="danger" items={outMembers} memberById={memberById} testid="list-out" />
        )}
        {pendingMembers.length > 0 && (
          <PendingList
            title={`No Response (${pendingMembers.length})`}
            members={pendingMembers}
            canManage={canManage}
            onResponse={respond}
            disabled={isClosed}
          />
        )}
      </div>

      <GuestSheet
        open={guestOpen}
        onClose={() => setGuestOpen(false)}
        poll={poll}
        members={members}
        onAdded={(p) => { setPoll(p); api.get("/members").then(({ data }) => setMembers(data)).catch(() => {}); }}
      />

      {/* Vote sheet */}
      <VoteSheet
        open={!!respondOpen}
        status={respondOpen}
        onClose={() => setRespondOpen(false)}
        onSubmit={(comment) => respond(respondOpen, comment)}
      />

      {/* Post-charges sheet */}
      <PostChargesSheet
        open={chargesOpen}
        onClose={() => setChargesOpen(false)}
        pollId={id}
        defaultFee={poll.match_fee}
        defaultSponsor={poll.sponsor_contribution}
        inCount={inMembers.length}
        onDone={load}
      />

      {/* Swap sheet */}
      {poll.teams && (
        <SwapSheet
          open={swapOpen}
          onClose={() => setSwapOpen(false)}
          teams={poll.teams}
          memberName={memberName}
          onSwap={async (a, b) => {
            try { await api.post(`/polls/${id}/swap`, { member_a: a, member_b: b }); toast.success("Swapped"); setSwapOpen(false); await load(); }
            catch (err) { toast.error(apiError(err)); }
          }}
        />
      )}
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-ink-muted font-semibold flex items-center gap-1">
        <Icon size={12}/> {label}
      </div>
      <div className="text-base font-bold mt-1 truncate">{value}</div>
    </div>
  );
}

function Stat({ label, value, tone = "default" }) {
  const tones = {
    default: "text-ink",
    success: "text-brand",
    danger: "text-red-500",
    warning: "text-amber-600",
  };
  return (
    <div className="rounded-2xl bg-white border border-black/[0.06] shadow-card p-3 text-center">
      <div className="text-[10px] uppercase tracking-widest text-ink-muted font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-0.5 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function VoteBtn({ label, tone, icon: Icon, onClick, active, testid }) {
  const bg = active
    ? tone === "success" ? "bg-brand text-white"
    : tone === "danger" ? "bg-red-500 text-white"
    : "bg-amber-500 text-white"
    : "bg-black/[0.04] text-ink";
  return (
    <button
      onClick={onClick}
      className={`h-14 rounded-2xl font-bold text-sm btn-press flex flex-col items-center justify-center gap-0.5 ${bg}`}
      data-testid={testid}
    >
      <Icon size={18}/>
      {label}
    </button>
  );
}

function VoteSheet({ open, status, onClose, onSubmit }) {
  const [comment, setComment] = useState("");
  useEffect(() => { if (!open) setComment(""); }, [open]);
  return (
    <Sheet open={open} onClose={onClose} title={`Mark yourself ${(status || "").toUpperCase()}`}>
      <div className="space-y-4">
        <Textarea
          label="Comment (optional)"
          placeholder="I'll bring the balls / running 15 mins late…"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          data-testid="vote-comment"
        />
        <Button size="lg" className="w-full" onClick={() => onSubmit(comment)} data-testid="vote-submit">
          Submit response
        </Button>
      </div>
    </Sheet>
  );
}

function PostChargesSheet({ open, onClose, pollId, defaultFee, defaultSponsor, inCount, onDone }) {
  const [total, setTotal] = useState("");
  const [sponsor, setSponsor] = useState(defaultSponsor || 0);
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTotal(defaultFee > 0 ? String(defaultFee * inCount) : "");
      setSponsor(defaultSponsor || 0);
      setDesc("");
    }
  }, [open, defaultFee, defaultSponsor, inCount]);

  const net = Math.max(Number(total || 0) - Number(sponsor || 0), 0);
  const perHead = inCount > 0 ? Math.round((net / inCount) * 100) / 100 : 0;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/polls/${pollId}/post-charges`, {
        total_amount: Number(total),
        sponsor_contribution: Number(sponsor) || 0,
        description: desc || null,
      });
      toast.success(`Charged ${inr(perHead)}/player`);
      onClose();
      await onDone?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Post match charges">
      <form onSubmit={submit} className="space-y-4">
        <Input label="Total Amount ₹ *" type="number" min="0" step="0.01" value={total} onChange={(e)=>setTotal(e.target.value)} required data-testid="charges-total" />
        <Input label="Sponsor Contribution ₹" type="number" min="0" step="0.01" value={sponsor} onChange={(e)=>setSponsor(e.target.value)} data-testid="charges-sponsor" />
        <Input label="Description" placeholder="Ground + Ball + Water" value={desc} onChange={(e)=>setDesc(e.target.value)} data-testid="charges-desc" />
        <Card className="bg-brand-50 border-brand-100">
          <div className="flex items-center justify-between text-sm">
            <span className="text-ink-muted">{inCount} IN players</span>
            <span className="font-bold text-brand">{inr(perHead)}/head</span>
          </div>
        </Card>
        <Button type="submit" size="lg" className="w-full" disabled={saving} data-testid="charges-submit">
          {saving ? "Posting…" : "Post to ledger"}
        </Button>
      </form>
    </Sheet>
  );
}

function ResponseList({ title, tone, items, memberById, testid, badge, canManage, promoting, onPromote, onRemove }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-2">
        {title}
      </div>
      <Card className="p-2 divide-y divide-black/[0.06]">
        {items.map((r) => {
          const m = memberById(r.member_id);
          const isWaiting = r.status === "waiting";
          return (
            <div key={r.id} className="flex items-center gap-3 px-2 py-2.5">
              <Avatar member={m || { name: "?" }} size={36} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                  <span className="truncate">{m?.name || "—"}</span>
                  {m?.guest && <Chip tone="warning">Guest</Chip>}
                </div>
                {r.comment && !m?.guest && <div className="text-xs text-ink-muted truncate">"{r.comment}"</div>}
              </div>
              <div className="flex items-center gap-2">
                {isWaiting && canManage && onPromote && (
                  <button
                    onClick={() => onPromote(r.id)}
                    disabled={promoting === r.id}
                    className="h-8 px-2.5 rounded-xl bg-brand-50 text-brand text-xs font-semibold btn-press hover:bg-brand hover:text-white transition-colors disabled:opacity-50"
                    data-testid={`promote-btn-${r.id}`}
                  >
                    {promoting === r.id ? "..." : "Promote"}
                  </button>
                )}
                <Chip tone={tone}>{r.status.toUpperCase()}</Chip>
                {m && onRemove && (
                  <button
                    onClick={() => onRemove(m)}
                    className="h-8 w-8 rounded-xl grid place-items-center text-ink-muted hover:bg-red-50 hover:text-red-500"
                    aria-label={`Remove ${m.name}`}
                    data-testid={`remove-player-${m.id}`}
                  >
                    <X size={15}/>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

function PendingList({ title, members, canManage, onResponse, disabled }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-2">
        {title}
      </div>
      <Card className="p-2 divide-y divide-black/[0.06]">
        {members.map((m) => (
          <div key={m.id} className="flex items-center gap-3 px-2 py-2.5" data-testid={`pending-row-${m.id}`}>
            <Avatar member={m} size={36} />
            <div className="flex-1 min-w-0 text-sm font-semibold truncate">{m.name}</div>
            {canManage && !disabled && (
              <div className="flex items-center gap-1">
                <IconMini onClick={() => onResponse("in", "", m.id)} tone="success" testid={`admin-vote-in-${m.id}`}><Check size={14}/></IconMini>
                <IconMini onClick={() => onResponse("maybe", "", m.id)} tone="warning" testid={`admin-vote-maybe-${m.id}`}><HelpCircle size={14}/></IconMini>
                <IconMini onClick={() => onResponse("out", "", m.id)} tone="danger" testid={`admin-vote-out-${m.id}`}><X size={14}/></IconMini>
              </div>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}

function IconMini({ children, onClick, tone, testid }) {
  const bg = tone === "success" ? "bg-brand-50 text-brand" : tone === "danger" ? "bg-red-50 text-red-500" : "bg-amber-50 text-amber-600";
  return (
    <button
      onClick={onClick}
      className={`h-8 w-8 rounded-xl grid place-items-center ${bg} btn-press`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function TeamsView({ teams, memberById }) {
  const renderSide = (side, title) => {
    const list = teams[side] || [];
    const cap = side === "team_a" ? teams.captain_a : teams.captain_b;
    const vc = side === "team_a" ? teams.vc_a : teams.vc_b;
    return (
      <Card className="p-3 flex-1 min-w-0">
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold">{title}</div>
          {teams.locked && <Chip tone="dark"><Lock size={10}/> Locked</Chip>}
        </div>
        <ul className="space-y-1.5">
          {list.map((mid) => {
            const m = memberById(mid);
            const isCap = mid === cap;
            const isVc = mid === vc;
            return (
              // Two narrow columns on a phone: the name gets the full width, tags go underneath
              <li key={mid} className="px-2.5 py-1.5 rounded-xl bg-black/[0.03] min-w-0">
                <div className="text-sm font-semibold truncate">{m?.name || "—"}</div>
                {(isCap || isVc || m?.guest) && (
                  <div className="flex flex-wrap gap-1 mt-0.5 text-[10px] font-bold">
                    {isCap && <span className="inline-flex items-center gap-0.5 text-brand-700"><Crown size={10}/> Captain</span>}
                    {isVc && <span className="inline-flex items-center gap-0.5 text-amber-700"><Star size={10}/> Vice-captain</span>}
                    {m?.guest && <span className="text-amber-700">Guest</span>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Card>
    );
  };
  return (
    <div data-testid="teams-view">
      <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-2">
        Auto-generated teams
      </div>
      <div className="flex gap-3">
        {renderSide("team_a", "Team A")}
        {renderSide("team_b", "Team B")}
      </div>
      {teams.common && (() => {
        const m = memberById(teams.common);
        return (
          <Card className="p-3 mt-3 flex items-center gap-2" data-testid="teams-common">
            <Avatar member={m || { name: "?" }} size={28} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{m?.name || "—"}</div>
              <div className="text-[11px] text-ink-muted">Odd number of players — plays for both teams</div>
            </div>
            {m?.guest && <Chip tone="warning">Guest</Chip>}
            <span className="px-1.5 py-px rounded-md bg-sky-100 text-sky-700 text-[10px] font-bold uppercase tracking-wide">Common</span>
          </Card>
        );
      })()}
    </div>
  );
}

function SwapSheet({ open, onClose, teams, memberName, onSwap }) {
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  useEffect(() => { if (!open) { setA(null); setB(null); } }, [open]);

  return (
    <Sheet open={open} onClose={onClose} title="Swap two players">
      <div className="space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-1.5">Team A</div>
          <div className="grid grid-cols-2 gap-2">
            {(teams.team_a || []).map((mid) => (
              <button key={mid} type="button" onClick={() => setA(mid)}
                data-testid={`swap-a-${mid}`}
                className={`h-11 rounded-2xl text-sm font-semibold text-left px-3 truncate ${a === mid ? "bg-brand text-white" : "bg-black/[0.04]"}`}
              >{memberName(mid)}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-1.5">Team B</div>
          <div className="grid grid-cols-2 gap-2">
            {(teams.team_b || []).map((mid) => (
              <button key={mid} type="button" onClick={() => setB(mid)}
                data-testid={`swap-b-${mid}`}
                className={`h-11 rounded-2xl text-sm font-semibold text-left px-3 truncate ${b === mid ? "bg-brand text-white" : "bg-black/[0.04]"}`}
              >{memberName(mid)}</button>
            ))}
          </div>
        </div>
        <Button size="lg" className="w-full" disabled={!a || !b} onClick={() => onSwap(a, b)} data-testid="swap-confirm-btn">
          Swap {a && b ? `${memberName(a)} ↔ ${memberName(b)}` : ""}
        </Button>
      </div>
    </Sheet>
  );
}

function MatchDay({ pollId }) {
  const nav = useNavigate();
  const [matches, setMatches] = useState(null);
  useEffect(() => {
    api.get("/scores", { params: { poll_id: pollId } })
      .then(({ data }) => setMatches([...data].reverse()))
      .catch(() => setMatches([]));
  }, [pollId]);

  if (matches === null) return <Skeleton className="h-24" />;
  const hasLive = matches.some((m) => m.status !== "completed");

  return (
    <div data-testid="match-day">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold">
          Match day {matches.length > 0 && `• ${matches.length} match${matches.length === 1 ? "" : "es"}`}
        </div>
        {matches.length > 0 && (
          <button
            onClick={() => nav(`/polls/${pollId}/report`)}
            className="text-xs font-semibold text-brand flex items-center gap-1"
            data-testid="day-report-btn"
          >
            <BarChart3 size={13}/> Day report
          </button>
        )}
      </div>
      <div className="space-y-3">
        {matches.map((m) => <ScoreListCard key={m.id} s={m} onClick={() => nav(`/scores/${m.id}`)} />)}
        <Button
          variant={hasLive ? "outline" : "dark"}
          className="w-full"
          onClick={() => nav(`/scores/new?poll=${pollId}`)}
          data-testid="poll-score-match-btn"
        >
          <Activity size={16}/> {matches.length ? `Start match ${matches.length + 1}` : "Start scoring"}
        </Button>
      </div>
    </div>
  );
}

function GuestSheet({ open, onClose, poll, members, onAdded }) {
  const [name, setName] = useState("");
  const [team, setTeam] = useState(null);
  const [saving, setSaving] = useState(false);
  const teams = poll?.teams;

  useEffect(() => {
    if (!open) return;
    setName("");
    // Default to the smaller team so sides stay even
    setTeam(teams ? ((teams.team_a?.length || 0) <= (teams.team_b?.length || 0) ? "a" : "b") : null);
  }, [open, teams]);

  if (!open) return null;
  const inPoll = new Set((poll.responses || []).map((r) => r.member_id));
  const pastGuests = members.filter((m) => m.guest && !inPoll.has(m.id));

  const add = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/polls/${poll.id}/guests`, { ...body, team });
      onAdded(data);
      toast.success("Guest added");
      onClose();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open onClose={onClose} title="Add guest player">
      <div className="space-y-4">
        <p className="text-sm text-ink-muted">
          Guests are marked IN, can be put in a team, and share the match charges like everyone else.
          They don't count towards your member limit.
        </p>
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) add({ name: name.trim() }); }} className="space-y-3">
          <Input label="Guest name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rahul (Amit's friend)" maxLength={60} autoFocus data-testid="guest-name" />
          {teams && (
            <div>
              <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-1.5">Add to team</div>
              <div className="grid grid-cols-2 gap-2">
                {[["a", "Team A", teams.team_a], ["b", "Team B", teams.team_b]].map(([k, label, list]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setTeam(k)}
                    className={`h-11 rounded-2xl text-sm font-semibold ${team === k ? "bg-ink text-white" : "bg-black/[0.04]"}`}
                    data-testid={`guest-team-${k}`}
                  >
                    {label} ({(list || []).length})
                  </button>
                ))}
              </div>
            </div>
          )}
          {poll.charges_posted && (
            <div className="text-xs text-amber-700 bg-amber-50 rounded-xl p-2.5">
              Charges were already posted for this poll, so this guest won't be charged automatically.
            </div>
          )}
          <Button type="submit" size="lg" className="w-full" disabled={saving || !name.trim()} data-testid="guest-add-submit">
            <UserPlus size={16}/> {saving ? "Adding…" : "Add guest"}
          </Button>
        </form>

        {pastGuests.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-1.5">Played with us before</div>
            <div className="flex flex-wrap gap-2">
              {pastGuests.map((g) => (
                <button
                  key={g.id}
                  onClick={() => add({ member_id: g.id })}
                  disabled={saving}
                  className="h-9 px-3 rounded-xl bg-amber-50 text-amber-800 text-sm font-semibold"
                  data-testid={`past-guest-${g.id}`}
                >
                  + {g.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}
